import React, { useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { iconNames } from "@/constants/iconNames";
import { useAuth } from "@/contexts/auth";
import { debugLogger } from '@/utils/debug';
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminEmptyState, AdminPanel } from "@/components/admin/AdminSurface";
import iconMap from "@/components/icons/iconMap";
import {
  AlertCircle,
} from "lucide-react";

const IconsPage: React.FC = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");

  if (authLoading) {
    return <AdminLayout title="Icon Browser" loading />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  const validIconNames = iconNames.filter((name) => iconMap[name]);
  const invalidIcons = iconNames.filter((iconName) => !iconMap[iconName]);

  const filteredIconNames = useMemo(() => {
    if (!searchTerm) return validIconNames;
    return validIconNames.filter(name =>
      name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, validIconNames]);

  debugLogger.log("Available mapped icons:", Object.keys(iconMap));

  return (
    <AdminLayout
      title="Icon Browser"
      description="Browse the Lucide icons currently referenced by category and listing UI."
    >
      <AdminPanel
        title="Available Icons"
        description={`${filteredIconNames.length} of ${validIconNames.length} matched the current search.`}
        actions={
          <Input
            placeholder="Search icons..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full min-w-[220px] bg-white md:w-[280px]"
          />
        }
      >
        {filteredIconNames.length === 0 ? (
          <AdminEmptyState
            icon={AlertCircle}
            title="No icons found"
            description="Try a different search term to find a matching icon name."
            className="min-h-[260px]"
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
            {filteredIconNames.map((iconName) => {
              const Icon = iconMap[iconName];

              return (
                <div
                  key={iconName}
                  className="flex min-h-[120px] flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-4 text-center"
                >
                  {Icon ? <Icon className="h-8 w-8 text-gray-800" /> : <span className="text-3xl">?</span>}
                  <span className="mt-3 text-xs font-medium text-gray-600">{iconName}</span>
                </div>
              );
            })}
          </div>
        )}
      </AdminPanel>

      {invalidIcons.length > 0 ? (
        <AdminPanel
          title="Missing Icon Mappings"
          description="These configured icon names do not currently map to the imported Lucide icon set."
        >
          <ul className="grid gap-2 text-sm text-red-600 sm:grid-cols-2 lg:grid-cols-3">
            {invalidIcons.map((iconName) => (
              <li key={iconName} className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                {iconName}
              </li>
            ))}
          </ul>
        </AdminPanel>
      ) : null}
    </AdminLayout>
  );
};

export default IconsPage;
